import React, { useState } from "react";
import ManualPdfButton from "@/components/ManualPdfButton";
import {
  BookOpen, House, FileText, CalendarBlank, Buildings, Users as UsersIcon,
  ShieldCheck, Factory, Scales, ShieldStar, Archive, ClockClockwise,
  BookOpen as BookIcon, Key, MagnifyingGlass, Warning, Lightbulb,
  CheckCircle, Info, Question, Upload, ChartBar, TreeStructure,
} from "@phosphor-icons/react";

/* ============================================================
 * Manual de usuario — ZAE Documental
 * Contenido estático, sin llamadas al backend. Accesible desde
 * el sidebar. Diseño de dos columnas: navegación + contenido.
 * ============================================================ */

const SECTIONS = [
  { id: "intro",         label: "Introducción",         icon: Info },
  { id: "omv",           label: "Objetivo · Misión · Visión", icon: Lightbulb },
  { id: "empezar",       label: "Cómo empezar",         icon: Key },
  { id: "dashboard",     label: "Dashboard",            icon: House },
  { id: "instalaciones", label: "Instalaciones",        icon: Factory },
  { id: "obligaciones",  label: "Obligaciones",         icon: Scales },
  { id: "documentos",    label: "Documentos",           icon: FileText },
  { id: "import",        label: "Importación CSV / Excel", icon: Upload },
  { id: "zip",           label: "Carga masiva ZIP",     icon: Archive },
  { id: "cumplimiento",  label: "Cumplimiento",         icon: ShieldStar },
  { id: "tarjeta",       label: "Tarjeta Informativa",  icon: ChartBar },
  { id: "calendario",    label: "Calendario y alertas", icon: CalendarBlank },
  { id: "empresa",       label: "Perfil de empresa",    icon: Buildings },
  { id: "usuarios",      label: "Usuarios y permisos",  icon: UsersIcon },
  { id: "auditoria",     label: "Auditoría",            icon: ClockClockwise },
  { id: "catalogos",     label: "Catálogos",            icon: BookIcon },
  { id: "estructuras",   label: "Estructuras Organizacionales", icon: TreeStructure },
  { id: "faq",           label: "Preguntas frecuentes", icon: Question },
];

export default function Manual() {
  const [active, setActive] = useState("intro");

  return (
    <div className="space-y-6" data-testid="manual-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Ayuda</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter flex items-center gap-3">
          <BookOpen size={36} weight="duotone" className="text-blue-600"/>
          Manual de usuario
        </h1>
        <p className="text-neutral-500 mt-2">Guía completa de todas las funcionalidades de ZAE Documental.</p>
      </div>
      <ManualPdfButton sections={SECTIONS}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* Sidebar de navegación */}
        <aside className="lg:sticky lg:top-4 self-start">
          <div className="bg-white border border-neutral-200 rounded-md p-2" data-testid="manual-nav">
            <ul>
              {SECTIONS.map(s => {
                const Ic = s.icon;
                return (
                  <li key={s.id}>
                    <button
                      onClick={()=>{
                        setActive(s.id);
                        document.getElementById(`sec-${s.id}`)?.scrollIntoView({ behavior:"smooth", block:"start" });
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                        active === s.id
                          ? "bg-blue-50 text-blue-700 font-semibold"
                          : "text-neutral-600 hover:bg-neutral-100"
                      }`}
                      data-testid={`manual-nav-${s.id}`}
                    >
                      <Ic size={16} weight="duotone"/>
                      {s.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* Contenido */}
        <div className="space-y-8" id="manual-content">
          <Section id="intro" title="¿Qué es ZAE Documental?" icon={Info}>
            <p>
              ZAE Documental es un sistema de gestión documental y de cumplimiento regulatorio,
              diseñado especialmente para empresas del sector energético que necesitan controlar
              obligaciones ante autoridades como <b>ASEA</b>, <b>CNE</b>, <b>SENER</b>, <b>SEMARNAT</b>,
              entre otras.
            </p>
            <p>Con ZAE Documental puedes:</p>
            <Bullets items={[
              "Centralizar todos los documentos de tu empresa y sus plantas.",
              "Vincular cada documento a la obligación regulatoria que cumple.",
              "Automatizar avisos de vencimiento y renovación.",
              "Flujo de aprobación con múltiples aprobadores y firmas.",
              "Historial completo de cambios y trazabilidad por empresa.",
            ]}/>
          </Section>

          <Section id="omv" title="Objetivo · Misión · Visión" icon={Lightbulb}>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-md border border-blue-100 bg-blue-50/60 p-4">
                <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700 mb-2">Objetivo</div>
                <p className="text-sm text-neutral-700 leading-relaxed">
                  Centralizar y automatizar el ciclo completo de gestión documental y de
                  cumplimiento regulatorio, garantizando <b>trazabilidad</b>, <b>vigencia</b> y
                  <b> disponibilidad</b> de la información crítica ante autoridades del sector energético.
                </p>
              </div>
              <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-4">
                <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700 mb-2">Misión</div>
                <p className="text-sm text-neutral-700 leading-relaxed">
                  Simplificar la administración de obligaciones de las empresas de distribución
                  y carburación de GLP mediante una plataforma <b>segura</b>, <b>colaborativa</b> y
                  <b> auditable</b>, que reduzca riesgos regulatorios y aumente la productividad
                  operativa.
                </p>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-4">
                <div className="text-[11px] uppercase tracking-widest font-bold text-amber-700 mb-2">Visión</div>
                <p className="text-sm text-neutral-700 leading-relaxed">
                  Convertirnos en el <b>estándar de referencia</b> en gestión documental y
                  cumplimiento regulatorio para el sector energético, adoptado por todas
                  las plantas y unidades operativas que aspiren a la excelencia normativa.
                </p>
              </div>
            </div>
            <Tip>
              Estos principios guían cada funcionalidad del sistema: trazabilidad completa,
              seguridad por diseño y experiencia clara para todos los perfiles de usuario.
            </Tip>
          </Section>

          <Section id="empezar" title="Cómo empezar" icon={Key}>
            <Steps steps={[
              "Ingresa con tu correo y contraseña desde la pantalla de inicio de sesión.",
              "Selecciona la empresa activa en el switcher superior derecho (si tienes acceso a varias).",
              "Configura el perfil de tu empresa y asigna al menos un aprobador (Perfil de empresa).",
              "Crea tus instalaciones (Instalaciones) y las obligaciones aplicables (Obligaciones).",
              "Empieza a subir documentos, individuales o mediante ZIP.",
            ]}/>
            <Tip>
              Si eres super_admin puedes activar el <b>modo oscuro</b> con el botón 🌙 del encabezado.
              El sistema recordará tu preferencia entre sesiones.
            </Tip>
          </Section>

          <Section id="dashboard" shot="dashboard" title="Dashboard" icon={House}>
            <p>
              El Dashboard es la vista general del estado de cumplimiento de la empresa activa.
              Muestra <b>KPIs</b> de documentos vigentes, próximos a vencer, vencidos y pendientes
              de aprobación, además de próximos vencimientos ordenados por prioridad.
            </p>
            <Tip>El Dashboard respeta la empresa activa o, si hay estructura organizacional activa, el <b>nodo seleccionado</b> (consolida todas las empresas del nodo, incluido el total de empresas y usuarios).</Tip>
          </Section>

          <Section id="instalaciones" shot="instalaciones" title="Instalaciones" icon={Factory}>
            <p>Registra cada planta, bodega o unidad operativa de la empresa.</p>
            <Steps steps={[
              "Presiona “Nueva instalación”.",
              "Captura nombre, código interno, tipo, dirección y responsable.",
              "Guarda: la instalación queda disponible para vincularse a obligaciones y documentos.",
            ]}/>
            <Tip>
              El campo <b>Tipo</b> se alimenta del catálogo “Tipos de instalación”. Puedes agregar
              nuevos tipos desde <i>Catálogos</i> (solo super_admin).
            </Tip>
            <Highlight color="blue">
              <b>Importación masiva</b>: con el botón <b>Importar</b> (junto a “Nueva instalación”)
              puedes cargar varias instalaciones desde un archivo <b>.csv</b> o <b>.xlsx</b>.
              Ver sección <i>Importación CSV / Excel</i>.
            </Highlight>
            <h4 className="font-bold text-neutral-800 mt-4">Detalle de la instalación</h4>
            <Bullets items={[
              "Al abrir una instalación verás tres pestañas: Información general, Obligaciones y Documentos.",
              "Las obligaciones y documentos se consultan dentro de la misma ventana (sin salir del detalle).",
              "Al crear una instalación la empresa activa (o la única del nodo) queda seleccionada por defecto; si el nodo agrupa varias empresas se muestra el selector limitado a ellas.",
            ]}/>
            <h4 className="font-bold text-neutral-800 mt-4">Eliminar y reasignar</h4>
            <Bullets items={[
              "No se puede eliminar una instalación con obligaciones o documentos relacionados: el sistema indica cuántos tiene.",
              "Administrador y Super Admin pueden usar “Reasignar” (icono ⇄) para mover todas sus obligaciones y documentos a otra instalación de la misma empresa y, opcionalmente, eliminar la original en el mismo paso.",
              "Si un administrador intenta eliminar una instalación con relaciones, el cuadro de reasignación se abre automáticamente.",
              "Cada reasignación queda registrada en Auditoría (acción “Reasignó”, con origen, destino y cantidades).",
            ]}/>
          </Section>

          <Section id="obligaciones" shot="obligaciones" title="Obligaciones" icon={Scales}>
            <p>
              Cada obligación regulatoria describe lo que la empresa debe cumplir ante una autoridad.
              El listado se organiza en forma jerárquica <b>Empresa › Autoridad › Obligaciones</b>,
              mostrando únicamente las empresas del nodo o empresa activa (incluso las que aún no tienen
              obligaciones, con la leyenda “Sin obligaciones registradas”).
            </p>
            <Bullets items={[
              "Al entrar todo aparece colapsado; despliega cada empresa y luego cada autoridad (CNE, ASEA, SENER…).",
              "Cada empresa muestra su contador de obligaciones y autoridades.",
              "Botones “Expandir todo” / “Colapsar todo” para abrir o cerrar todo de una vez.",
              "Al cambiar de nodo en el encabezado, las empresas y sus obligaciones se actualizan automáticamente.",
              "Al crear o editar una obligación, su empresa y autoridad se despliegan y la fila se resalta para ubicarla.",
            ]}/>
            <h4 className="font-bold text-neutral-800 mt-4">Documentos, semáforo y filtros</h4>
            <Bullets items={[
              "Columna “Documentos”: indica cuántos documentos tiene la obligación. Al dar clic se abre un panel lateral con sus documentos (Ver, Descargar, Abrir detalle) sin salir del listado.",
              "Columna “Estado” (semáforo) según el documento más reciente: 🔴 Vencido · 🟠 Por vencer (30 días o menos) · 🟢 Vigente · ⚪ Sin vencimiento.",
              "Las obligaciones sin documentos se resaltan en ámbar con la leyenda “Requiere documento”, y cada empresa indica cuántas tiene sin documentos.",
              "Filtro de estado: Todas / Sin documentos / Vencidas / Por vencer / Vigentes (con contador). Al elegir uno se despliegan automáticamente las empresas y autoridades con coincidencias.",
            ]}/>
            <Steps steps={[
              "Crea una obligación con “Nueva obligación”: nombre, autoridad, frecuencia, responsable.",
              "Vincúlala opcionalmente a una instalación específica.",
              "Desde el detalle podrás agregar actividades (checklist) y documentos vinculados.",
            ]}/>
            <Highlight color="blue">
              <b>Plantilla oficial GLP</b>: los super_admin pueden presionar <b>“Cargar plantilla GLP”</b>
              para sembrar automáticamente 61 obligaciones oficiales de CNE, ASEA y SENER.
              La operación es idempotente (no duplica).
            </Highlight>
            <h4 className="font-bold text-neutral-800 mt-4">Eliminar y reasignar documentos</h4>
            <Bullets items={[
              "No se puede eliminar una obligación que tenga documentos relacionados.",
              "Administrador y Super Admin pueden “Reasignar documentos” (icono ⇄) a otra obligación de la misma empresa; los documentos toman también la instalación de la obligación destino.",
              "Opcionalmente se elimina la obligación original en el mismo paso. Todo queda registrado en Auditoría.",
            ]}/>
          </Section>

          <Section id="documentos" shot="documentos" title="Documentos" icon={FileText}>
            <p>
              Cada documento cargado en el sistema mantiene: nombre, autoridad, periodo, fechas de
              emisión/vigencia/vencimiento, responsable, aprobador, estado y bitácora completa.
            </p>
            <Steps steps={[
              "Desde el módulo Documentos presiona “Nuevo documento” para abrir el wizard.",
              "Llena metadatos y adjunta el archivo (PDF, XLSX, imagen, etc.).",
              "Al guardar, el documento entra en estado “Pendiente” hasta que un aprobador lo revise.",
              "Puedes editar sus metadatos mientras esté Pendiente o Rechazado; se resetea el flujo.",
            ]}/>
            <Bullets items={[
              "Estados de vigencia: Vigente · Próximo a vencer · Vencido (calculado automáticamente).",
              "Flujo de aprobación con historial de intentos, comentarios y reasignación.",
              "Notificaciones por correo (Mailjet) a todos los aprobadores configurados.",
              "Alarmas automáticas en el Calendario a 60, 30, 0 y +7 días del vencimiento.",
              "La empresa del documento se toma de su obligación o instalación vinculada.",
            ]}/>
            <h4 className="font-bold text-neutral-800 mt-4">Agrupación Empresa › Autoridad</h4>
            <Bullets items={[
              "La tabla se organiza por empresa (solo las del nodo o empresa activa) y, dentro de cada una, por autoridad (ASEA, CNE, SENER…), cada grupo con su contador.",
              "Al entrar todo aparece colapsado; da clic en la empresa y luego en la autoridad para ver sus documentos.",
              "“Expandir todo” / “Colapsar todo” abren o cierran todos los grupos de una vez.",
              "Al escribir en el buscador los grupos se despliegan automáticamente mostrando solo las coincidencias; los filtros de periodo, autoridad y género reagrupan la tabla.",
              "Los documentos sin empresa asignada aparecen en el grupo “Sin empresa”.",
              "Acciones por fila como iconos (pasa el cursor para ver su nombre): Visualizar, Descargar, Editar y Eliminar.",
            ]}/>
            <h4 className="font-bold text-neutral-800 mt-4">Columnas de la tabla</h4>
            <p>
              Documento · Periodo (año) · Autoridad · <b>Emisión</b> · <b>Vigencia</b> · <b>Vencimiento</b> ·
              Género · Estatus · Aprobación · Creado. Las fechas se muestran en formato dd/mm/aaaa; el
              vencimiento aparece en rojo si está vencido y en ámbar si está próximo a vencer.
            </p>
            <h4 className="font-bold text-neutral-800 mt-4">Editar fechas de varios documentos</h4>
            <Steps steps={[
              "Marca las casillas de los documentos (o la casilla del encabezado para seleccionar todos los editables visibles; respeta filtros y búsqueda).",
              "En la barra azul pulsa “Editar fechas (N)”.",
              "Captura Emisión, Vigencia y/o Vencimiento: solo se modifican los campos que llenes.",
              "Pulsa “Aplicar fechas”. El estatus (vigente / por vencer / vencido) se recalcula automáticamente.",
            ]}/>
            <Highlight color="amber">
              Solo aplica a documentos <b>Pendientes o Rechazados</b> (los aprobados tienen la casilla deshabilitada).
              El Editor puede editar los documentos que él subió; Administrador y Super Admin, cualquiera de su alcance.
              El vencimiento no puede ser anterior a la emisión. Si algún documento se omite, el sistema muestra la lista con el motivo.
              Cada cambio queda registrado en Auditoría.
            </Highlight>
          </Section>

          <Section id="import" title="Importación CSV / Excel" icon={Upload}>
            <p>
              Dos módulos permiten carga masiva mediante hoja de cálculo con vista previa antes
              de persistir: <b>Empresas</b> (panel Super Admin) e <b>Instalaciones</b>.
            </p>
            <h4 className="font-bold text-neutral-800 mt-3">Flujo general</h4>
            <Steps steps={[
              "Entra al módulo correspondiente y presiona el botón “Importar”.",
              "Descarga la plantilla CSV (opcional) para ver las columnas aceptadas.",
              "Sube el archivo .csv o .xlsx y presiona “Vista previa”.",
              "Revisa la tabla: cada fila se marca como Nueva, Actualizar o Error.",
              "Presiona “Confirmar (N)” para persistir solo las filas válidas.",
            ]}/>

            <h4 className="font-bold text-neutral-800 mt-4">Importar empresas</h4>
            <Bullets items={[
              "Ruta: Panel Super Admin → botón “Importar empresas”.",
              "Columnas: name (obligatorio), tax_id, email, phone, address, logo_url, description. (El logo también puede subirse después desde el Perfil de Empresa.)",
              "Alias en español soportados: nombre, rfc, correo, teléfono, dirección, descripción.",
              "Duplicados: si el tax_id ya existe, la empresa se actualiza; si no, se crea.",
            ]}/>

            <h4 className="font-bold text-neutral-800 mt-4">Importar instalaciones</h4>
            <Bullets items={[
              "Ruta: módulo Instalaciones → botón “Importar”.",
              "Columnas: name (obligatorio), company_name o company_tax_id, type, code, address, responsible, description.",
              "Alias en español: nombre, empresa, rfc, tipo, permiso, dirección, responsable.",
              "Para super_admin la columna company_name o company_tax_id es obligatoria. Para otros roles la instalación se asigna automáticamente a la empresa activa.",
              "Duplicados: coincidencia por (empresa + permiso); si no hay permiso, por (empresa + nombre). Las coincidencias se actualizan.",
            ]}/>

            <Highlight color="amber">
              Las filas marcadas como <b>Error</b> se omiten al importar. Puedes volver a
              “Cambiar archivo” desde la vista previa para corregir y reintentar sin cerrar el modal.
            </Highlight>
          </Section>

          <Section id="zip" title="Carga masiva mediante ZIP" icon={Archive}>
            <p>
              Puedes importar múltiples documentos manteniendo la estructura de carpetas del ZIP.
              Cada archivo se registra como <b>un documento independiente</b>, no como parte del ZIP.
            </p>
            <Steps steps={[
              "Entra al detalle de una obligación y ve a la pestaña “Documentos”.",
              "Presiona “Importar ZIP” y selecciona el archivo.",
              "Indica el periodo y (opcional) el responsable por defecto.",
              "Confirma: cada archivo se sube y se crea un documento en la carpeta correspondiente.",
            ]}/>
            <Highlight color="amber">
              <b>Importante</b>: cada documento importado conserva el 100% de las funcionalidades
              (vigencia, responsable, aprobador, versionado, tareas). Puedes editarlos de forma
              independiente igual que los cargados uno por uno.
            </Highlight>
            <p className="text-xs text-neutral-500">Ejemplo de estructura reconocida:</p>
            <pre className="bg-neutral-900 text-neutral-100 rounded-md p-3 text-xs overflow-x-auto">
{`SASISOPA.zip
├── I Política
│   ├── Política SASISOPA.pdf
│   └── Carta compromiso.pdf
├── II Identificación P&AR
│   └── Análisis de riesgos.pdf
└── III Requisitos legales
    └── Matriz legal.xlsx`}
            </pre>
          </Section>

          <Section id="cumplimiento" shot="cumplimiento" title="Cumplimiento" icon={ShieldStar}>
            <p>
              Vista consolidada de todas las obligaciones y su estado documental (Vigente,
              Próximo, Vencido, Sin documento). Ideal para auditorías internas.
            </p>
            <Tip>Los reportes pueden exportarse a Excel y PDF con el botón “Exportar”.</Tip>
          </Section>

          <Section id="tarjeta" shot="tarjeta" title="Tarjeta Informativa (GLP)" icon={ChartBar}>
            <p>
              Reporte gráfico tipo “tanque” que muestra el porcentaje de cumplimiento por autoridad
              (<b>CNE</b>, <b>ASEA</b>, <b>SENER</b>) comparando el año actual contra el anterior.
            </p>
            <Bullets items={[
              "Cada tanque representa una autoridad y se llena con el % de obligaciones cubiertas.",
              "Debajo del tanque se muestra la variación (▲ ▼) contra el año anterior.",
              "Puedes cambiar el año desde el selector superior para analizar históricos.",
              "Una obligación cuenta como “cubierta” si tiene al menos un documento aprobado y vigente del periodo.",
            ]}/>
            <Tip>
              Ideal para presentar un vistazo ejecutivo del estado de cumplimiento en una sola pantalla.
            </Tip>
          </Section>

          <Section id="calendario" shot="calendario" title="Calendario y alertas" icon={CalendarBlank}>
            <p>
              El calendario centraliza eventos manuales y alarmas automáticas de vencimientos.
              Puedes verlo en <b>modo grid</b> (mes) o <b>lista</b>.
            </p>
            <Bullets items={[
              "Alarmas automáticas: 60 días antes, 30 días antes, día del vencimiento y +7 días.",
              "Notificaciones por correo (diarias/semanales/mensuales según el nivel).",
              "Puedes crear eventos manuales con “Enviar prueba” para validar la configuración.",
            ]}/>
          </Section>

          <Section id="empresa" shot="empresa" title="Perfil de empresa" icon={Buildings}>
            <p>Configura los datos generales de tu empresa y los <b>aprobadores</b>.</p>
            <Steps steps={[
              "Actualiza nombre, RFC, dirección, contacto y descripción.",
              "Logo: usa “Subir logo” / “Cambiar logo” / “Quitar”. Acepta PNG, JPG o WEBP de hasta 2 MB; se ajusta automáticamente a 512×512 px.",
              "En Flujo de aprobación, usa el buscador para agregar uno o varios aprobadores.",
              "Guarda cambios: cualquiera de los aprobadores podrá aprobar/rechazar documentos.",
            ]}/>
            <Tip>Todos los aprobadores reciben las notificaciones por correo del mismo documento.</Tip>
            <Highlight color="blue">
              <b>¿Quién puede editar?</b> Cualquier usuario con permiso <b>Perfil de Empresa › Escritura</b>
              (Super Admin, Administrador y Editor por defecto), únicamente sobre sus empresas asignadas o
              las del nodo activo. El Visualizador solo consulta.
            </Highlight>
          </Section>

          <Section id="usuarios" shot="usuarios" title="Usuarios, permisos y multiempresa" icon={UsersIcon}>
            <p>
              Gestión granular por módulo con acceso a múltiples empresas/plantas por usuario.
            </p>
            <Bullets items={[
              "Roles: Super Admin, Administrador, Editor, Visualizador.",
              "Al elegir el rol, la matriz de permisos se pre-rellena con los permisos del rol y puedes ampliarla manualmente.",
              "El rol define un mínimo garantizado: esas casillas aparecen con candado y no pueden quitarse (Editor = ver + editar en módulos operativos; Visualizador = solo ver; Administrador = todo salvo Super Admin).",
              "Permisos efectivos: matriz módulo × Lectura / Escritura / Eliminación (mínimo del rol + lo que agregues).",
              "Empresas asignadas: multi-select con empresa por defecto (★) para iniciar sesión.",
            ]}/>
            <h4 className="font-bold text-neutral-800 mt-4">Plantillas predefinidas</h4>
            <Bullets items={[
              "Administrador: acceso completo a todos los módulos.",
              "Supervisor: RW en operativos, R en configuración.",
              "Capturista: RW en instalaciones/obligaciones/documentos/calendario. Sin eliminar.",
              "Consulta: solo lectura de módulos operativos.",
            ]}/>
            <h4 className="font-bold text-neutral-800 mt-4">Contraseñas seguras</h4>
            <Bullets items={[
              "Mínimo 8 caracteres.",
              "Al menos una mayúscula, una minúscula, un número y un carácter especial.",
              "Botón ✨ “Sugerir” genera una contraseña de 12 caracteres que cumple con la política.",
              "Botón 📋 “Copiar” la envía al portapapeles.",
            ]}/>
          </Section>

          <Section id="auditoria" shot="auditoria" title="Bitácora de auditoría" icon={ClockClockwise}>
            <p>
              Registro cronológico de todas las acciones del sistema. Incluye
              <b> filtros por empresa, usuario, módulo, acción y rango de fechas</b>.
            </p>
            <Bullets items={[
              "Búsqueda libre por texto en detalle o nombre de usuario.",
              "Cada registro muestra la empresa desde la cual se ejecutó la acción.",
              "Exportación a CSV compatible con Excel (BOM UTF-8).",
              "Registra también reasignaciones (“Reasignó”, marcadas [Origen] / [Destino]) y cambios de logo.",
            ]}/>
          </Section>

          <Section id="catalogos" shot="catalogos" title="Catálogos" icon={BookIcon}>
            <p>
              Catálogos globales que alimentan los selects del sistema (autoridades, categorías,
              tipos documentales, tipos de instalación, frecuencias).
            </p>
            <Bullets items={[
              "Reordenables mediante arrastrar y soltar, botones ↑↓ o índice numérico.",
              "Índices duplicados se resuelven alfabéticamente como criterio secundario.",
              "Botón “Normalizar índices” reasigna 0..N según el orden actual.",
            ]}/>
          </Section>

          <Section id="estructuras" shot="estructura" title="Estructuras organizacionales" icon={TreeStructure}>
            <p>
              Permite construir jerarquías reutilizando las empresas ya registradas, sin duplicarlas. Útil para representar
              distintos recortes operativos: grupo completo, operación por marca, operación regional, etc.
            </p>

            <h4 className="font-bold text-neutral-800 mt-3">Niveles disponibles</h4>
            <Bullets items={[
              "L1 País · L2 División País · L3 Zona · L6 Razón Social · L7 Sucursal.",
              "Los niveles 4 y 5 quedan reservados para expansión futura (ej. Región, Distrito).",
              "L1–L6 son etiquetas de texto libre (tú escribes el nombre). L7 Sucursal vincula una empresa existente del sistema.",
              "El filtrado se calcula a partir de las empresas (L7) que cuelgan del nodo seleccionado.",
            ]}/>

            <h4 className="font-bold text-neutral-800 mt-4">Crear una estructura</h4>
            <Steps steps={[
              "Entra a “Estructuras Org.” en el sidebar (solo super_admin).",
              "Presiona “Nueva estructura”, captura nombre y selecciona qué niveles tendrá.",
              "Dentro del detalle, presiona “Agregar nodo raíz” para crear el primer nivel.",
              "En cada nodo, pasa el cursor y usa el botón “+ Nivel siguiente” para anidar hijos.",
              "Para L7 Sucursal el modal muestra el catálogo de empresas existentes para que selecciones las que correspondan.",
            ]}/>

            <h4 className="font-bold text-neutral-800 mt-4">Reglas</h4>
            <Bullets items={[
              "Una empresa (sucursal) puede pertenecer a múltiples estructuras simultáneamente — no se duplican registros.",
              "Los niveles L1–L6 son etiquetas libres (texto) y no afectan la BD de empresas o instalaciones.",
              "Al activar una estructura, el filtrado toma las empresas (L7) del nodo elegido; si una estructura no tiene L7, no restringe nada.",
              "Eliminar un nodo elimina también sus descendientes (en cascada) dentro de esa estructura.",
              "Activar/Desactivar una estructura no afecta la información original de empresas o instalaciones.",
            ]}/>

            <h4 className="font-bold text-neutral-800 mt-4">Encabezado y contexto activo</h4>
            <Bullets items={[
              "La barra superior muestra el logo, el nombre de la organización (estructura activa o empresa activa) y la etiqueta Estructura › Nodo activo.",
              "Haz click en la etiqueta de estructura, o en “Navegar estructura” del menú lateral, para abrir el panel de navegación.",
              "El nodo seleccionado persiste al navegar entre módulos y al volver a entrar.",
            ]}/>

            <h4 className="font-bold text-neutral-800 mt-4">Panel “Navegar estructura”</h4>
            <Steps steps={[
              "Elige la estructura con las etiquetas superiores (la seleccionada se resalta en rojo).",
              "Usa el buscador para encontrar un nodo, empresa o código; se muestran las coincidencias con su ruta.",
              "Selecciona “Toda la estructura” para ver la información consolidada, o cualquier nodo del árbol (▸ para expandir).",
              "El número a la derecha de cada nodo indica cuántas empresas incluye.",
            ]}/>
            <Bullets items={[
              "Fijar (chincheta): deja el panel acoplado a la izquierda, sin fondo oscuro, mientras navegas; se recuerda al recargar.",
              "Cerrar (✕), click fuera del panel o tecla Esc lo cierran.",
              "“Quitar estructura” vuelve a tu empresa por defecto.",
              "El cambio de nodo se aplica al instante, sin recargar la página.",
            ]}/>

            <h4 className="font-bold text-neutral-800 mt-4">¿Qué filtra el nodo elegido?</h4>
            <Bullets items={[
              "Al seleccionar un nodo intermedio (ej. Zona) se consolida la información de todas las empresas que cuelgan de él.",
              "Aplica a Dashboard, Instalaciones, Obligaciones, Documentos, Cumplimiento, Calendario, Notificaciones y exportaciones.",
              "Detalle y edición: los registros fuera del nodo muestran “Fuera del nodo/empresa seleccionado”.",
              "Creación: los nuevos registros solo pueden pertenecer a empresas del nodo.",
              "Usuarios en modo estructuras solo ven las estructuras que tienen autorizadas.",
            ]}/>

            <Tip>
              Puedes <b>duplicar</b> una estructura desde el catálogo para variar la jerarquía
              sin perder la original.
            </Tip>
          </Section>

          <Section id="faq" title="Preguntas frecuentes" icon={Question}>
            <FAQ q="¿Puedo tener el mismo usuario con acceso a varias empresas?"
                 a="Sí. Desde el formulario de usuario selecciona todas las empresas y establece una como “por defecto”. El usuario podrá alternarlas desde el switcher del encabezado."/>
            <FAQ q="¿Cómo cambio mi contraseña?"
                 a="Un super_admin o administrador debe editar tu usuario en el módulo Usuarios y usar el botón ‘Sugerir’ o capturar una nueva contraseña que cumpla la política."/>
            <FAQ q="¿Qué pasa si edito un documento aprobado?"
                 a="Puedes editar metadatos en estado Pendiente o Rechazado; si cambias el archivo se resetea el intento a Pendiente y se notifica a los aprobadores."/>
            <FAQ q="¿Se pueden borrar registros de la bitácora de auditoría?"
                 a="No. La bitácora es inmutable por diseño para garantizar trazabilidad regulatoria."/>
            <FAQ q="¿Qué autoridades vienen precargadas?"
                 a="El catálogo incluye SEMARNAT, STPS, IMSS, INFONAVIT, SAT, PROFEPA, Municipal, entre otras. Al cargar la plantilla GLP se agregan CNE, ASEA y SENER."/>
            <FAQ q="¿Los correos de notificación funcionan en producción?"
                 a="Sí, se envían mediante Mailjet desde no-reply@tomza.com con SPF/DKIM configurado."/>
            <FAQ q="¿Qué formatos acepta la importación de empresas e instalaciones?"
                 a="Archivos .csv (UTF-8) y .xlsx. Las columnas aceptan alias en español (nombre, rfc, empresa, permiso, etc.). Siempre hay una vista previa antes de guardar."/>
            <FAQ q="¿Por qué no puedo eliminar una instalación u obligación?"
                 a="Porque tiene obligaciones o documentos relacionados. Un Administrador o Super Admin puede reasignarlos con el botón ⇄ y eliminar la original en el mismo paso."/>
            <FAQ q="¿Por qué veo “Fuera del nodo/empresa seleccionado”?"
                 a="El registro pertenece a una empresa que no está en el nodo activo. Cambia de nodo desde el panel “Navegar estructura” o selecciona “Toda la estructura”."/>
            <FAQ q="¿Por qué no puedo desmarcar un permiso de un usuario?"
                 a="Las casillas con candado son el mínimo garantizado por su rol. Para quitarlas cambia el rol del usuario (por ejemplo, de Editor a Visualizador)."/>
            <FAQ q="¿Cómo atiendo primero las obligaciones sin documentos o vencidas?"
                 a="En Obligaciones usa el filtro “Sin documentos” o “Vencidas”: se despliegan solo las coincidencias. Da clic en el contador de documentos para revisarlos en el panel lateral."/>
            <FAQ q="No veo mis documentos en la tabla, ¿dónde están?"
                 a="La tabla inicia colapsada: da clic en la empresa y después en la autoridad, usa “Expandir todo” o escribe en el buscador para desplegar las coincidencias."/>
            <FAQ q="¿Por qué no puedo seleccionar un documento para editar sus fechas?"
                 a="La edición en bloque solo aplica a documentos pendientes o rechazados de los que eres responsable (o a cualquiera de tu alcance si eres Administrador)."/>
            <FAQ q="¿Qué tamaño debe tener el logo de la empresa?"
                 a="PNG, JPG o WEBP de hasta 2 MB. El sistema lo reduce automáticamente a un máximo de 512×512 px."/>
            <FAQ q="¿Qué pasa si importo un archivo con filas incompletas?"
                 a="La vista previa marca cada fila como Nueva, Actualizar o Error. Las filas con error (ej. nombre faltante o empresa inexistente) se omiten; las válidas se procesan."/>
          </Section>
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers de presentación ---------- */

function Section({ id, title, icon: Icon, shot, children }) {
  return (
    <section id={`sec-${id}`} className="manual-section bg-white border border-neutral-200 rounded-md p-6 scroll-mt-4" data-testid={`manual-section-${id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 text-blue-700 flex items-center justify-center">
          <Icon size={20} weight="duotone"/>
        </div>
        <h2 className="font-display text-2xl font-black tracking-tighter">{title}</h2>
      </div>
      {shot && (
        <figure className="manual-shot mb-4 rounded-md overflow-hidden border border-neutral-200 bg-neutral-50" data-testid={`manual-shot-${id}`}>
          <img src={`/manual/${shot}.jpeg`} alt={`Pantalla: ${title}`} loading="lazy" className="w-full h-auto block"/>
          <figcaption className="px-3 py-1.5 text-[11px] text-neutral-500 border-t border-neutral-200 bg-white">Pantalla de referencia · {title}</figcaption>
        </figure>
      )}
      <div className="space-y-3 text-sm leading-relaxed text-neutral-700 [&_b]:text-neutral-900 [&_a]:text-blue-600">
        {children}
      </div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5 marker:text-blue-500">
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  );
}

function Steps({ steps }) {
  return (
    <ol className="space-y-2">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-md bg-blue-600 text-white font-bold text-xs flex items-center justify-center">{i+1}</span>
          <span className="flex-1">{s}</span>
        </li>
      ))}
    </ol>
  );
}

function Tip({ children }) {
  return (
    <div className="flex gap-2 text-xs bg-blue-50 border border-blue-100 rounded-md p-3 mt-3">
      <Lightbulb size={16} weight="duotone" className="text-blue-600 flex-shrink-0 mt-0.5"/>
      <div className="text-neutral-700">{children}</div>
    </div>
  );
}

function Highlight({ color, children }) {
  const map = {
    blue: "bg-blue-50 border-blue-100 text-neutral-700",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
  };
  return (
    <div className={`text-sm border rounded-md p-3 mt-2 ${map[color] || map.blue}`}>
      <div className="flex items-start gap-2">
        {color === "amber"
          ? <Warning size={16} weight="duotone" className="text-amber-600 flex-shrink-0 mt-0.5"/>
          : <CheckCircle size={16} weight="duotone" className="text-blue-600 flex-shrink-0 mt-0.5"/>}
        <div>{children}</div>
      </div>
    </div>
  );
}

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="border border-neutral-200 rounded-md p-3" onToggle={(e)=>setOpen(e.currentTarget.open)}>
      <summary className="cursor-pointer font-semibold text-neutral-800 flex items-center gap-2 list-none">
        <MagnifyingGlass size={14} className={open?"text-blue-600":"text-neutral-400"}/>
        {q}
      </summary>
      <p className="mt-2 text-sm text-neutral-600 pl-6">{a}</p>
    </details>
  );
}
